import AddNewSchool from "components/main-view/utils/create-edit-school";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { MdCorporateFare } from "react-icons/md";
import { getCollectionRequest } from "services/apiRequests";
import { LoadingDots } from "utils/loadingAnimation";
import { singlify } from "utils/utils";

interface SchoolProfileProps {
  ref: any;
  schoolId: string;
  setShowSuccessfulSchoolEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  headerRef: React.RefObject<any>;
  subHeaderRef: React.RefObject<any>;
}

const SchoolProfile: React.FC<SchoolProfileProps> = forwardRef(
  (
    { schoolId, setShowSuccessfulSchoolEdit, setActiveItem, headerRef, subHeaderRef },
    ref
  ) => {
    // Get the context parameters
    const [schoolData, setSchoolData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const { setShowSideBarMenu, setContextSchool } = useContext(MainContext);
    const { schoolTerminology } = usePlatform();

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      onBackButtonClick() {
        onDiscardCreateEditSchool();
      },
      onDiscard() {
        onDiscardCreateEditSchool();
      },
      pressSaveButton() {
        handleSave();
      },
    }));

    // Define a reference
    const addCustomerRef: React.Ref<any> = useRef(null);

    const getSchoolData = async () => {
      const response = await getCollectionRequest(`/api/organisations/profile?schoolId=${schoolId}`);
      if (!response.successful) {
        return undefined;
      }
      return response.content;
    };

    /**
     * Root definition
     */
    useEffect(() => {
      getSchoolData()
        .then(schoolData => {
          setSchoolData(schoolData);
          setIsLoading(false);
          console.log(schoolData);
          setHeaderTitleAndDescription(
            schoolData ? schoolData.name : "-",
            `${singlify(schoolTerminology)} Panel`
          );
        });
    }, []);

    /**
     * Function used to set title and description
     * @param title
     * @param description
     */
    const setHeaderTitleAndDescription = (
      title: string,
      description: string
    ) => {
      // Set the title and description
      headerRef.current.setHeaderTitle(title);
      headerRef.current.setHeaderDescription(
        <div className="w-fit flex gap-1">
          <MdCorporateFare />
          <div>{description}</div>
        </div>
      );
    };

    /**
     * Function that defines discard behaviour when
     * creating/editing schools
     */
    const onDiscardCreateEditSchool = () => {
      // Activate the side bar menu
      setShowSideBarMenu(true);
      // And force unselect the context school (used for master admin)
      setContextSchool(null);
      // And come back to dashboard
      setActiveItem("dashboard");

      // (Main Header) Change the titles and back button
      setHeaderTitleAndDescription(schoolTerminology, `List of current ${schoolTerminology.toLowerCase()}`);
      headerRef.current.setShowBackButton(false);

      // (Dashboard Header) Change the button settings
      // Delete the generate report button
      headerRef.current.options().setShowGenerateReportsButton(false);
      // Activating the create new button
      headerRef.current.options().setShowCreateNewButton(true);
      // And turn off the other buttons
      headerRef.current
        .options()
        .setSaveButton({ show: false, disabled: true });
      headerRef.current.options().setCreateTypeButton("customers");

      // And force hide the subheader
      subHeaderRef.current.setShow(false);
    };

    /**
     * Function that coordinates the save function depending on
     * the internal state of the component
     */
    const handleSave = () => {
      addCustomerRef.current.handleSaveSchool();
    };

    /**
     * Function used after save a school
     */
    const afterSaveFunction = async () => {
      // Display external successful message
      setShowSuccessfulSchoolEdit(true);

      // Discard the create edit view
      onDiscardCreateEditSchool();
    };

    return (
      <>
        {isLoading ? (
          <LoadingDots/>
        ) : (
          <AddNewSchool
            ref={addCustomerRef}
            afterSaveFunction={afterSaveFunction}
            selectedData={schoolData}
            headerRef={headerRef}
          />
        )}
      </>
    );
  }
);

export default SchoolProfile;
